import { observer } from 'mobx-react'
import { GetServerSideProps, NextPage } from 'next'
import { useMemo } from 'react'
import { SEOHead } from '~/components/business/SEOHead'
import { GlobalRequest } from '~/components/global-request'
import ChatHistory from '~/components/pages/deepseek/ChatHistory'
import { MessageList } from '~/components/pages/deepseek/message/MessageList'
import { DeepseekSilent } from '~/components/pages/deepseek/DeepseekSilent'
import { Layout } from '~/layouts/default'
import DsInput from '~/components/pages/deepseek/dsInput'
import { useDeepSeekMotion } from '~/hooks/ai/useDeepSeekMotion'

import classNames from 'classnames'
import { useAtom } from 'jotai'
import {
  conversationIdAtom,
  searchResultsAtom,
  messageListRefAtom,
  curHistoryConversationAtom,
} from '~/components/pages/deepseek/atom'
import { SearchResult } from '~/components/pages/deepseek/SearchResult'
import { ConversationType } from '~/graphqls/types/gqls.global'
import { useRouterChangeStart } from '~/hooks/useRouterChangeStart'
import { useResetAtoms } from '~/components/pages/deepseek/hooks'
const DeepSeek: NextPage<{}> = () => {
  const [conversationId] = useAtom(conversationIdAtom)
  const [curHistoryConversation] = useAtom(curHistoryConversationAtom)
  const {
    messages,
    sendMessage,
    isTyping,
    isTypingRef,
    isThinking,
    stopMessage,
  } = useDeepSeekMotion()
  const resetAtoms = useResetAtoms()
  const [currentTyping, currentThinking] = useMemo(() => {
    if (!conversationId) return [false, false]
    return [
      isTyping?.[conversationId] ?? false,
      isThinking?.[conversationId] ?? false,
    ]
  }, [isTyping, isThinking, conversationId])

  const chatTitle = useMemo(() => {
    const title =
      curHistoryConversation?.title ||
      messages[0]?.title ||
      messages[0]?.content ||
      '新会话'
    return title.length > 30 ? title.slice(0, 30) + '...' : title
  }, [curHistoryConversation, messages])

  const [searchResults] = useAtom(searchResultsAtom)
  const [, setMessageListRef] = useAtom(messageListRefAtom)

  useRouterChangeStart(async () => {
    await stopMessage()
    resetAtoms()
  })
  return (
    <Layout
      headerType="no-margin"
      mainStyle={{
        height: 'calc(100vh - 56px)',
        minHeight: 'calc(100vh - 56px)',
      }}
      className="min-h-full "
      mainClassName="relative"
      headerClassName="border-b-[1px]  border-b-neutral-300"
      showFooter={false}
    >
      <SEOHead title="DeepSeek" />
      <div className="flex h-full">
        <ChatHistory
          stopMessage={stopMessage}
          conversationType={ConversationType.DEEPSEEK}
        />
        <div className="flex-auto flex flex-col justify-center relative bg-neutral-100">
          <div
            className={classNames(
              'absolute left-0 top-0 right-0 bottom-0 overflow-y-auto scrollbar flex',
              messages.length !== 0 && 'justify-between'
            )}
            ref={ref => setMessageListRef(ref)}
          >
            <div
              className={classNames(
                'flex-auto h-fit flex flex-col items-center min-h-full',
                messages.length === 0 && 'justify-center'
              )}
            >
              {messages.length !== 0 ? (
                <>
                  <div
                    className="sticky top-0 z-10 w-full  text-base font-semibold text-neutral-900 text-center py-3  text-overflow"
                    style={{
                      background:
                        'linear-gradient(180deg, #141416 54.17%, rgba(20, 20, 22, 0.00) 100%)',
                    }}
                  >
                    {chatTitle}
                  </div>
                  <MessageList
                    messages={messages}
                    isTyping={currentTyping}
                    isTypingRef={isTypingRef}
                    className="pt-12 w-[800px] flex-auto"
                  />
                </>
              ) : (
                <DeepseekSilent />
              )}
              <div className="sticky bottom-0 pb-6 bg-neutral-100 rounded-t-2xl w-[800px]">
                <DsInput
                  sendMessage={sendMessage}
                  stopMessage={stopMessage}
                  isTyping={currentTyping}
                  isThinking={currentThinking}
                />
              </div>
            </div>

            {!!searchResults?.length && (
              <div className="sticky z-[51] top-0 w-[302px] h-full py-6 mr-6 ml-1">
                <SearchResult />
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  )
}

export const getServerSideProps: GetServerSideProps = async context => {
  const { client, initialState, isLogin, loginRedirect } = await GlobalRequest(
    context
  )

  if (!isLogin) {
    return loginRedirect()
  }

  return {
    props: {
      initialState,
    },
  }
}

export default observer(DeepSeek)
